import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

// Fade Up Animation
export const FadeUp = ({ children, delay = 0, duration = 0.5, className = "", ...props }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{
        duration,
        delay,
        ease: [0.215, 0.61, 0.355, 1.0], // smooth cubic-bezier
      }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Stagger Container
export const StaggerContainer = ({ children, delayChildren = 0, staggerChildren = 0.1, className = "", ...props }) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={{
        hidden: {},
        show: {
          transition: {
            delayChildren,
            staggerChildren,
          },
        },
      }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Stagger Item (used inside Stagger Container)
export const StaggerItem = ({ children, duration = 0.5, className = "", ...props }) => {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 20 },
        show: {
          opacity: 1,
          y: 0,
          transition: {
            ease: [0.215, 0.61, 0.355, 1.0],
            duration,
          },
        },
      }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Counter Animation (Numbers count up when scrolled into view)
export const Counter = ({ from = 0, to, duration = 1.5, suffix = "", prefix = "", delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [count, setCount] = useState(from);

  useEffect(() => {
    if (isInView && to !== undefined) {
      const controls = animate(from, to, {
        duration,
        delay,
        ease: "easeOut",
        onUpdate: (value) => {
          // If the target number is a float (like 4.9), show 1 decimal place
          if (to % 1 !== 0) {
            setCount(parseFloat(value.toFixed(1)));
          } else {
            setCount(Math.floor(value));
          }
        },
      });
      return () => controls.stop();
    }
  }, [isInView, from, to, duration, delay]);

  // Format count nicely (e.g. adds commas for thousands)
  const formattedCount = to % 1 !== 0 ? count : Math.floor(count).toLocaleString();

  return (
    <span ref={ref} className={className}>
      {prefix}
      {formattedCount}
      {suffix}
    </span>
  );
};

// Text Reveal Animation (reveals words one by one as they come into view)
export const TextReveal = ({ text, delay = 0, duration = 0.4, className = "" }) => {
  if (!text) return null;
  const words = text.split(" ");
  
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.04,
        delayChildren: delay,
      },
    },
  };

  const wordVariants = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.span
      variants={containerVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      className={`inline-block ${className}`}
    >
      {words.map((word, idx) => (
        <motion.span
          key={idx}
          variants={wordVariants}
          className="inline-block mr-[0.25em]"
        >
          {word}
        </motion.span>
      ))}
    </motion.span>
  );
};

// Hover Card Animation (lifts card slightly, adds subtle zoom)
export const HoverCard = ({ children, scale = 1.02, y = -6, duration = 0.25, className = "", ...props }) => {
  return (
    <motion.div
      whileHover={{ scale, y }}
      transition={{ duration, ease: "easeOut" }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};
